<template>
  <div class="cal-month">
    <div class="cal-month-weekdays">
      <span v-for="label in weekdayLabels" :key="label" class="cal-month-weekday">
        {{ label }}
      </span>
    </div>
    <div class="cal-month-grid">
      <button
        v-for="cell in cells"
        :key="cell.id"
        type="button"
        class="cal-month-cell"
        :class="{ 'is-out': !cell.inMonth, 'is-today': isToday(cell.id) }"
        @click="$emit('select-day', cell.id)"
      >
        <span class="cal-month-daynum">{{ dayNum(cell.id) }}</span>
        <div class="cal-month-titles">
          <div
            v-for="(todo, i) in titlesFor(cell.id)"
            :key="todo.id || i"
            class="cal-month-title"
            :class="{ checked: todo.checked }"
          >
            {{ todo.text }}
          </div>
        </div>
      </button>
    </div>
  </div>
</template>

<script>
import moment from "moment";
const { monthGridDays } = require("./calendarGrid.js");
const { isToday } = require("./dayNav.js");

export default {
  name: "CalendarMonthView",
  props: {
    anchorDay: {
      type: String,
      required: true,
    },
    weekStartsOnMonday: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["select-day"],
  computed: {
    cells() {
      if (!this.anchorDay) return [];
      return monthGridDays(this.anchorDay, this.weekStartsOnMonday);
    },
    weekdayLabels() {
      const lang = this.$store.getters.config.language;
      const monFirst = this.weekStartsOnMonday;
      // moment: 0=Sun … 6=Sat; build Mon-first or Sun-first short names
      const order = monFirst ? [1, 2, 3, 4, 5, 6, 0] : [0, 1, 2, 3, 4, 5, 6];
      return order.map((dow) =>
        moment().locale(lang).day(dow).format("dd")
      );
    },
  },
  methods: {
    isToday,
    dayNum(id) {
      return moment(String(id), "YYYYMMDD").date();
    },
    titlesFor(id) {
      const lists = this.$store.getters.todoLists || {};
      const list = lists[id];
      return Array.isArray(list) ? list : [];
    },
  },
};
</script>

<style scoped>
.cal-month {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 6px;
}

.cal-month-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  flex: 0 0 auto;
  padding: 0 2px;
}

.cal-month-weekday {
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: var(--m-muted, rgba(60, 60, 67, 0.55));
}

.cal-month-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-auto-rows: minmax(72px, 1fr);
  gap: 4px;
  flex: 1 1 auto;
  min-height: 0;
}

.cal-month-cell {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 72px;
  min-width: 0;
  padding: 4px;
  border: 1px solid var(--m-hairline, rgba(60, 60, 67, 0.12));
  border-radius: 8px;
  background: var(--m-surface, #fff);
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.cal-month-cell.is-out {
  opacity: 0.45;
}

.cal-month-cell.is-today {
  border-color: rgba(13, 110, 253, 0.5);
  box-shadow: inset 0 0 0 1px rgba(13, 110, 253, 0.25);
}

.cal-month-daynum {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 2px;
}

.cal-month-titles {
  flex: 1 1 auto;
  min-height: 0;
  max-height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.cal-month-title {
  font-size: 11px;
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 1px 0;
}

.cal-month-title.checked {
  text-decoration: line-through;
  opacity: 0.55;
}

.dark-theme .cal-month-cell {
  background: var(--m-surface, #1c1c1e);
}

.dark-theme .cal-month-cell.is-today {
  border-color: rgba(10, 132, 255, 0.55);
  box-shadow: inset 0 0 0 1px rgba(10, 132, 255, 0.3);
}

@media (min-width: 768px) {
  .cal-month-grid {
    grid-auto-rows: minmax(96px, 1fr);
    gap: 6px;
  }

  .cal-month-cell {
    min-height: 96px;
    padding: 6px;
  }

  .cal-month-title {
    font-size: 12px;
  }
}
</style>
